import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { WebsiteAuditService } from '../website-audit.service';
import { mapOpportunityRows } from './opportunities.utils';

const SECTION_KEY = 'opportunities';

@Component({
  selector: 'app-opportunities',
  templateUrl: './opportunities.component.html',
  styleUrls: ['../audit-theme.css', './opportunities.component.css'],
})
export class OpportunitiesComponent implements OnInit, OnDestroy {
  constructor(private websiteAuditService: WebsiteAuditService) {}

  private subs = new Subscription();
  // Overlay shown while the sub-bar swaps competitors.
  public competitorSwitching$ =
    this.websiteAuditService.getCompetitorSwitching();

  public isLoading = false;
  public hasData = false;

  public label = 'Opportunities';
  public compName = '';
  public total = 0;
  public threshold: number | null = null;
  public metaText = '';

  // Rows in the API's (already prioritized) order, and the sorted view of them.
  private allItems: any[] = [];
  public items: any[] = [];
  public sortKey = '';
  public sortOrder: 'asc' | 'desc' = 'asc';

  ngOnInit(): void {
    this.subs.add(
      this.websiteAuditService.getAuditTasksLoading().subscribe((v: any) => {
        this.isLoading = v;
      }),
    );

    this.subs.add(
      this.websiteAuditService.getSelectedAuditTask().subscribe((task: any) => {
        this.build(task);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ─── Build ───────────────────────────────────────────────────────────────

  private build(task: any) {
    const section = task?.sections?.[SECTION_KEY] || null;
    const list = Array.isArray(section?.items) ? section.items : [];

    this.hasData = !!section && list.length > 0;
    if (!this.hasData) {
      this.allItems = [];
      this.items = [];
      this.total = 0;
      this.threshold = null;
      this.metaText = '';
      return;
    }

    this.label = section?.label || 'Opportunities';
    this.total =
      typeof section?.total === 'number' ? section.total : list.length;
    this.threshold =
      typeof section?.threshold === 'number' ? section.threshold : null;
    this.compName = this.getDomain(
      task?.selectedCompetitor?.domain || task?.selectedCompetitor?.url || '',
    );

    this.allItems = mapOpportunityRows(list, this.compName);

    // `total` counts everything the audit flagged; `items` can be a capped
    // slice of it, so only say "of N" when rows are actually missing.
    this.metaText =
      (this.total > list.length
        ? `${list.length} of ${this.total} shown`
        : `${list.length} opportunit${list.length === 1 ? 'y' : 'ies'}`) +
      (this.threshold !== null ? ` · scoring below ${this.threshold}` : '');

    this.applySort();
  }

  // ─── Sort ────────────────────────────────────────────────────────────────

  sortData(key: string): void {
    if (this.sortKey === key) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // The list arrives already prioritized, so a fresh 'desc' would look
      // unchanged — start ascending so the rows visibly react on first click.
      this.sortKey = key;
      this.sortOrder = 'desc';
    }
    this.applySort();
  }

  private applySort(): void {
    if (!this.sortKey) {
      this.items = [...this.allItems];
      return;
    }
    const dir = this.sortOrder === 'asc' ? 1 : -1;
    const value = (o: any) =>
      this.sortKey === 'score'
        ? // Nulls sort last either way.
          (o.score ?? Number.MAX_SAFE_INTEGER)
        : o.confidenceRank;
    this.items = [...this.allItems].sort((a, b) => (value(a) - value(b)) * dir);
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  private getDomain(url: string): string {
    if (!url) return '';
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;
      return new URL(normalized).hostname.replace(/^www\./, '');
    } catch {
      return url.replace(/^www\./, '');
    }
  }
}
